import React, { useState, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useDropzone } from "react-dropzone";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import "./UploadStatus.scss";
import Svg from "../svg/Svg";
import ProgressBar from "../progress-bar/ProgressBar";
import { Success, Error, Retry } from "../svg/svgImageList";
import useThemeService from "../../services/theme-service/ThemeService";
import { translationService } from "../../services/translation/TranslationService";
import { Collapse as CollapseIcon } from "../svg/svgImageList";
import { uploadFilesService } from "../../services/upload/UploadFilesService";
import {
  FileExtended,
  FileUploadTrackerWrapper
} from "../../services/upload/FileUploadTracker";
import { InspectionFileNormError, ParserResult } from "../../services/upload/UploadFilesModels";
import { useSort } from "../../helpers/useSort";
import { SortDirection } from "../datatable/interfaces";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import useUrlParams from "../../services/url/UrlParamsService";
import DownloadButton from "../buttons/download-button/DownloadButton";
import useMediaFilesService from "../../services/mediafiles/MediaFilesStorageService";

export default observer(function UploadStatus(props:{rowAction?:any}) {
  const { themeClass } = useThemeService();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const files = useMemo(() => uploadFilesService.filesUploadStatus, [uploadFilesService.filesStatusRenderTrigger]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const uploadStatus = useMemo(() => uploadFilesService.uploadProgress.uploadStatus, [uploadFilesService.filesStatusRenderTrigger]);

  return (
    <section className="upload-status-container">
      <h3 className="upload-status-title">
        {translationService.translate("general.upload.status-modal.title", {
          filesCount: files.length,
        })}
      </h3>
      <div className={themeClass(["upload-result-summary"], ["success"])}>
        <Svg svg={Success} width={20} />
        <span className="upload-summary-text success">
          {translationService.translate("general.upload.success", {
            filesCount: uploadStatus.completed + uploadStatus.completedWithWarnings,
          })}
        </span>
      </div>
      <div className={themeClass(["upload-result-summary"], ["error"])}>
        <Svg svg={Error} width={20} />
        <span className="upload-summary-text">
          {translationService.translate("general.upload.fail", { filesCount: uploadStatus.failed })}
        </span>
      </div>
      <UploadStatusTable files={files} rowAction={props.rowAction}/>
    </section>
  );
});

enum UploadStatusSortableColumns {
  Name = "name",
  Status = "status",
}

const UploadStatusTable = ({ files, rowAction }: { files: Array<FileUploadTrackerWrapper>, rowAction?: any }) => {
  const { themeClass } = useThemeService();
  const { sortKey, sortDirection, sort, updateSort } = useSort();

  return (
    <table className={themeClass(["upload-status-table"])}>
      <thead>
        <tr>
          <th
            className="upload-status-file-name status-table-header"
            onClick={() => {
              updateSort(UploadStatusSortableColumns.Name);
            }}
          >
            {translationService.translate(`general.name`)}
            {sortKey === UploadStatusSortableColumns.Name ? (
              sortDirection === SortDirection.Asc ? (
                <ArrowUpwardIcon className="status-arrow" />
              ) : (
                <ArrowDownwardIcon className="status-arrow" />
              )
            ) : (
              <ArrowUpwardIcon className="show-on-hover-status-arrow" />
            )}
          </th>
          <th
            className="upload-status-progress status-table-header"
            onClick={() => {
              updateSort(UploadStatusSortableColumns.Status);
            }}
          >
            {translationService.translate("general.upload.status")}
            {sortKey === UploadStatusSortableColumns.Status ? (
              sortDirection === SortDirection.Asc ? (
                <ArrowUpwardIcon className="status-arrow" />
              ) : (
                <ArrowDownwardIcon className="status-arrow" />
              )
            ) : (
              <ArrowUpwardIcon className="show-on-hover-status-arrow" />
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {sort(files).map((file: FileUploadTrackerWrapper, index: number) => (
          <FileRow fileAction={rowAction} fileModel={file} key={`${file.wrapper.path}:${file.wrapper.name}:${index}`} />
        ))}
      </tbody>
    </table>
  );
};

const FileRow = (props: { fileModel: FileUploadTrackerWrapper, fileAction?: any }) => {
  const fileModel = props.fileModel.wrapper;
  const file = { ...props.fileModel.wrapper };
  const {getConvertedVideoSasUrl} = useMediaFilesService();

  const urlParamsService = useUrlParams();
  const { themeClass } = useThemeService();

  const getDownloadLink = useCallback(async () => {
    const link = await getConvertedVideoSasUrl(file.id!);
    props.fileAction!(link);
  }, [getConvertedVideoSasUrl, file, props.fileAction])

  const onChangeFile = (newFile: Array<File>) => {
    const mediaFile = fileModel;
    if (newFile.length !== 1 || newFile[0].name !== mediaFile.name) {
      snackbarNotificationsService.showError("general.upload.inspection-filename-not-match");
      return;
    }

    mediaFile.warning = undefined;
    mediaFile.error = undefined;
    mediaFile.canRetry = false;
    mediaFile.updateFileExtended(
      new FileExtended(newFile[0], (newFile[0] as unknown as FileExtended).path)
    );

    uploadFilesService.reuploadMediaFile(mediaFile, urlParamsService.getProjectId()!, mediaFile.id!);
  };

  const { getInputProps, open } = useDropzone({
    onDrop: onChangeFile,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: file.name.substring(file.name.lastIndexOf(".")),
  });
  const [openErrorSection, setOpenErrorSection] = useState(false);

  const handleOpenErrorSection = useCallback(() => {
    setOpenErrorSection((openErrorSection) => !openErrorSection);
  }, []);

  return (
    <>
      <tr className="upload-file-row">
        <td className="upload-status-file-name">{file.name}</td>
        <td
          className={themeClass(
            ["upload-status-progress"],
            [
              (file.error && "failed") ||
              (file.warning &&
                (file.warning.includes("success-with-warnings")
                  ? "success-with-warnings"
                  : "warning")) ||
              (!file.status && "not-started") ||
              "success",
            ]
          )}
        >
          {!file.status && `${translationService.translate("general.uploading")}...`}
          {file.status && file.status !== "ongoing" && (
            (file.status === "converting" && translationService.translate('general.converting')) ||
            (file.status === "converted" && translationService.translate('general.converted')) ||
            (file.warning && translationService.translate(file.warning)) ||
            (file.error && translationService.translate(file.error)) ||
            translationService.translate("general.uploaded")
          )}
          {file.status === "converted" && <DownloadButton onClick={getDownloadLink}/>}
          {file.status && file.status === "ongoing" && (
            <ProgressBar withText progress={Math.round(file.progress)} />
          )}
        </td>
        {(file.parsingErrorsDetails.length !== 0 || file.normError) && (
          <td onClick={handleOpenErrorSection}>
            <Svg
              svg={CollapseIcon}
              className={themeClass(
                ["secondary-menu-collapse"],
                [
                  !(
                    file.parsingErrorsDetails.length ||
                    file.normError?.invalidCodeSystem ||
                    file.normError?.invalidFormat ||
                    file.normError?.notSupportedCodeSystem ||
                    file.normError?.notSupportedFormat
                  )
                    ? "hidden-arrow"
                    : openErrorSection
                      ? "down-status-arrow"
                      : "up-status-arrow",
                ]
              )}
            />
          </td>
        )}
        {file.canRetry && (
          <td onClick={open}>
            <Svg svg={Retry} className={themeClass(["secondary-menu-collapse"])} />
            <input {...getInputProps()} />
          </td>
        )}
      </tr>
      {openErrorSection &&
        (file.normError ? (
          <NormErrorRow normError={file.normError} />
        ) : (
          <ParsingErrorTable parsingErrors={file.parsingErrorsDetails} />
        ))}
    </>
  );
};

enum ParsingErrorSortableColumns {
  Severity = "severity",
  RowNumber = "rowNumber",
  Text = "text",
}

const ParsingErrorTable = ({ parsingErrors }: { parsingErrors: ParserResult[] }) => {
  const { themeClass } = useThemeService();
  const { sortKey, sortDirection, sort, updateSort } = useSort(
    ParsingErrorSortableColumns.Severity
  );

  return (
    <table className={themeClass(["parsing-error-table"])}>
      <thead>
        <tr>
          <th
            className="error-details-severity status-table-header"
            onClick={() => {
              updateSort(ParsingErrorSortableColumns.Severity);
            }}
          >
            {translationService.translate("general.type")}
            {sortKey === ParsingErrorSortableColumns.Severity ? (
              sortDirection === SortDirection.Asc ? (
                <ArrowUpwardIcon className="status-arrow" />
              ) : (
                <ArrowDownwardIcon className="status-arrow" />
              )
            ) : (
              <ArrowUpwardIcon className="show-on-hover-status-arrow" />
            )}
          </th>
          <th
            className="error-details-line status-table-header"
            onClick={() => {
              updateSort(ParsingErrorSortableColumns.RowNumber);
            }}
          >
            {translationService.translate("general.line")}
            {sortKey === ParsingErrorSortableColumns.RowNumber ? (
              sortDirection === SortDirection.Asc ? (
                <ArrowUpwardIcon className="status-arrow" />
              ) : (
                <ArrowDownwardIcon className="status-arrow" />
              )
            ) : (
              <ArrowUpwardIcon className="show-on-hover-status-arrow" />
            )}
          </th>
          <th
            className="error-details-text status-table-header"
            onClick={() => {
              updateSort(ParsingErrorSortableColumns.Text);
            }}
          >
            {translationService.translate("general.message")}
            {sortKey === ParsingErrorSortableColumns.Text ? (
              sortDirection === SortDirection.Asc ? (
                <ArrowUpwardIcon className="status-arrow" />
              ) : (
                <ArrowDownwardIcon className="status-arrow" />
              )
            ) : (
              <ArrowUpwardIcon className="show-on-hover-status-arrow" />
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {sort(parsingErrors).map((errorDetails: ParserResult, index: number) => (
          <ParsingErrorRow key={index} errorDetails={errorDetails} />
        ))}
      </tbody>
    </table>
  );
};

const ParsingErrorRow = (props: { errorDetails: ParserResult }) => {
  const { errorDetails: errorDetail } = { ...props };

  return (
    <tr className="error-details-row">
      {errorDetail.severity && (
        <td className="error-details-severity">
          {translationService.translate(
            `general.inspectionFileUpload.error-severity.${errorDetail.severity}`
          )}
        </td>
      )}
      <td className="error-details-line">
        {errorDetail.rowNumber ? errorDetail.rowNumber.trim() : ""}
      </td>
      <td className="error-details-text">{translationService.translate(errorDetail.text)}</td>
    </tr>
  );
};

const NormErrorRow = (props: { normError: InspectionFileNormError }) => {
  const normError = { ...props.normError };

  return (
    <>
      <tr className="error-details-row">
        <td className="upload-status-file-name" />
        <td className="norm-error">
          {normError.invalidFormat && (
            <span className="norm-error-details-text">
              {translationService.translate("validation.inspectionFile-format.invalid", {
                invalidFormat: normError.invalidFormat,
              })}
            </span>
          )}
          {normError.notSupportedFormat && (
            <span className="norm-error-details-text">
              {translationService.translate("validation.inspectionFile-format.not-supported")}
            </span>
          )}
          {normError.invalidCodeSystem && (
            <span className="norm-error-details-text">
              {translationService.translate("validation.inspectionFile-codeSystem.invalid", {
                invalidCodeSystem: normError.invalidCodeSystem,
              })}
            </span>
          )}
          {normError.notSupportedCodeSystem && (
            <span className="norm-error-details-text">
              {translationService.translate("validation.inspectionFile-codeSystem.not-supported")}
            </span>
          )}
        </td>
      </tr>
    </>
  );
};
